import { buildOpenSignUpRows } from "@/artifacts/student/utils/dashboard/buildOpenSignupRows";
import { HStack } from "@/components/layout/HStack";
import { Badge } from "@/components/ui/Badge/Badge";
import { Button } from "@/components/ui/Button/Button";
import { DashboardCard } from "@/components/ui/DashboardCard/DashboardCard";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu/DropdownMenu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table/Table";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
} from "@tanstack/react-table";
import { format } from "date-fns";
import {
  ArrowDownAZ,
  ArrowUpAZ,
  ArrowUpDown,
  CirclePlus,
  Filter,
} from "lucide-react";
import { useMemo, useState } from "react";

export type OpenSignupRow = {
  rotationId: string;
  rotationDate: string;
  sessionId: string;
  sessionName: string;
  multiWeek: boolean;
};

const buildColumns = (
  onOpen: (rotationId: string, sessionId: string) => void,
): ColumnDef<OpenSignupRow>[] => [
  {
    accessorKey: "rotationDate",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Date{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDownAZ />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUpAZ />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    cell: ({ row }) => format(row.original.rotationDate, "MMMM dd, yyyy"),
    filterFn: (row, columnId, filterValue) => {
      const selected = (filterValue as string[]) ?? [];
      if (selected.length === 0) return true;

      const v = row.getValue(columnId);
      const dt = v instanceof Date ? v : new Date(String(v));
      if (Number.isNaN(dt.getTime())) return false;

      return selected.includes(dt.toISOString());
    },
    sortingFn: "datetime",
  },
  {
    accessorKey: "sessionName",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Session{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDownAZ />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUpAZ />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    filterFn: "arrIncludesSome",
  },
  {
    accessorKey: "multiWeek",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Multi-Week{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDownAZ />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUpAZ />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    cell: ({ row }) => {
      return row.original.multiWeek === true ? (
        <Badge variant="success">Yes</Badge>
      ) : (
        <Badge variant="destructive">No</Badge>
      );
    },
    filterFn: (row, columnId, value: ("yes" | "no")[] | undefined) => {
      if (!value || value.length === 0) return true;
      const v = row.getValue(columnId) as boolean;
      return value.includes(v ? "yes" : "no");
    },
  },
  {
    id: "actions",
    header: () => (
      <div style={{ textAlign: "right", fontWeight: "var(--font-medium)" }}>
        Actions
      </div>
    ),
    cell: ({ row }) => (
      <HStack justify="flex-end">
        <Button
          size="icon-small"
          onClick={() =>
            onOpen(row.original.rotationId, row.original.sessionId)
          }
        >
          <CirclePlus />
        </Button>
      </HStack>
    ),
  },
];

const DataTable = ({
  data,
  columns,
}: {
  data: OpenSignupRow[];
  columns: ColumnDef<OpenSignupRow>[];
}) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
    },
  });

  // Rotation Filtering
  const rotCol = table.getColumn("rotationDate");
  const selectedRot = (rotCol?.getFilterValue() as string[] | undefined) ?? [];

  const rotations = useMemo(() => {
    const map = new Map<string, Date>();

    for (const d of data) {
      const dt = new Date(d.rotationDate);
      if (!(dt instanceof Date) || Number.isNaN(dt.getTime())) continue;
      const iso = dt.toISOString();
      if (!map.has(iso)) map.set(iso, dt);
    }

    return Array.from(map.values()).sort((a, b) => a.getTime() - b.getTime());
  }, [data]);

  const toggleRot = (dt: Date) => {
    const iso = dt.toISOString();
    const next = selectedRot.includes(iso)
      ? selectedRot.filter((s) => s !== iso)
      : [...selectedRot, iso];

    rotCol?.setFilterValue(next);
  };

  const clearRotFilter = () => {
    rotCol?.setFilterValue([]);
  };

  // Session Filtering
  const seshCol = table.getColumn("sessionName");
  const selectedSesh =
    (seshCol?.getFilterValue() as string[] | undefined) ?? [];

  const sessions = useMemo(() => {
    const set = new Set<string>();
    for (const d of data) if (d.sessionName) set.add(d.sessionName);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const toggleSesh = (sesh: string) => {
    const next = selectedSesh.includes(sesh)
      ? selectedSesh.filter((s) => s !== sesh)
      : [...selectedSesh, sesh];

    seshCol?.setFilterValue(next);
  };

  const clearSeshFilter = () => {
    seshCol?.setFilterValue([]);
  };

  // Multi-Week
  type MultiWeekFilter = "yes" | "no";
  const multiWeekOptions: MultiWeekFilter[] = ["yes", "no"];

  const multiWeekCol = table.getColumn("multiWeek");
  const selectedMultiWeek =
    (multiWeekCol?.getFilterValue() as MultiWeekFilter[] | undefined) ?? [];

  const availableMultiWeek = useMemo(() => {
    const set = new Set<MultiWeekFilter>();
    for (const d of data) set.add(d.multiWeek ? "yes" : "no");
    return multiWeekOptions.filter((v) => set.has(v));
  }, [data]);

  const toggleMultiWeek = (v: MultiWeekFilter) => {
    const next = selectedMultiWeek.includes(v)
      ? selectedMultiWeek.filter((s) => s !== v)
      : [...selectedMultiWeek, v];

    multiWeekCol?.setFilterValue(next);
  };

  const clearMultiWeekFilter = () => {
    multiWeekCol?.setFilterValue([]);
  };

  const totalCount = table.getCoreRowModel().rows.length;
  const filteredCount = table.getFilteredRowModel().rows.length;

  return (
    <>
      <HStack align="center">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              style={{
                height: "fit-content",
                padding: "var(--space-xxs) var(--space-xs)",
              }}
            >
              <Filter /> Filter
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>Dates</DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {rotations.map((r) => {
                  const iso = r.toISOString();
                  return (
                    <DropdownMenuCheckboxItem
                      key={iso}
                      checked={selectedRot.includes(iso)}
                      onCheckedChange={() => toggleRot(r)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {format(r, "MMMM dd, yyyy")}
                    </DropdownMenuCheckboxItem>
                  );
                })}

                <DropdownMenuSeparator />

                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    clearRotFilter();
                  }}
                  disabled={selectedRot.length === 0}
                >
                  Clear
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>Sessions</DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {sessions.map((s) => (
                  <DropdownMenuCheckboxItem
                    key={s}
                    checked={selectedSesh.includes(s)}
                    onCheckedChange={() => toggleSesh(s)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {s}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    clearSeshFilter();
                  }}
                  disabled={selectedSesh.length === 0}
                >
                  Clear
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>Multi-Week</DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {availableMultiWeek.map((a) => (
                  <DropdownMenuCheckboxItem
                    key={a}
                    checked={selectedMultiWeek.includes(a)}
                    onCheckedChange={() => toggleMultiWeek(a)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {a === "yes" ? "Yes" : "No"}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    clearMultiWeekFilter();
                  }}
                  disabled={selectedMultiWeek.length === 0}
                >
                  Clear
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </DropdownMenuContent>
        </DropdownMenu>
        <p
          style={{
            color: "var(--muted-foreground)",
          }}
        >
          {filteredCount} of {totalCount} sessions
        </p>
      </HStack>
      <Table borderColor="var(--secondary-border)">
        <TableHeader>
          {table.getHeaderGroups().map((h) => (
            <TableRow key={h.id}>
              {h.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length}>No records</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </>
  );
};

const OpenSignupsTable = ({
  data,
  onOpen,
}: {
  data: {
    sessions: any[];
    rotations: any[];
  };
  onOpen: (rotationId: string, sessionId: string) => void;
}) => {
  const rows = useMemo(() => buildOpenSignUpRows(data), [data]);
  const columns = useMemo(() => buildColumns(onOpen), [onOpen]);

  return (
    <DashboardCard>
      <DataTable data={rows} columns={columns} />
    </DashboardCard>
  );
};

export { OpenSignupsTable };
